/**
 * B-485 — karta otwarta PRZED wdrożeniem nie kończy białym ekranem, gdy wejdzie
 * w ekran, którego jej stary build jeszcze nie pobrał.
 *
 * ── Dlaczego to powstało ──────────────────────────────────────────────────
 * Panel to SPA z ~50 ekranami ładowanymi leniwie (`lazy(() => import(...))` w
 * `App.tsx`). Każdy ekran ma własny plik z haszem w nazwie
 * (`/assets/HelpPage-AbC123.js`). Wdrożenie podmienia kontener `frontend`: nowy
 * obraz ma NOWE nazwy plików, a starych już nie ma — nginx odpowiada na nie 404.
 *
 * Od B-482 `index.html` jest rewalidowany (`Cache-Control: no-cache`), więc każde
 * KOLEJNE wejście na panel dostaje nowy build. Zostaje karta, która była otwarta
 * w chwili wdrożenia: biegnie na starym buildzie i zna tylko stare nazwy. Gdy
 * użytkownik kliknie w niej ekran, którego jeszcze nie otwierał, `import()` pada
 * („Failed to fetch dynamically imported module"), `React.lazy` odrzuca obietnicę,
 * a że w aplikacji nie ma ErrorBoundary, React odmontowuje CAŁE drzewo — biały
 * ekran do ręcznego F5. Deploy bywa kilka razy dziennie, więc to się zdarza.
 *
 * ── Co robimy ──────────────────────────────────────────────────────────────
 * Vite rzuca na `window` zdarzenie `vite:preloadError`, gdy nie uda się pobrać
 * pliku ekranu (albo jego stylów). Przeładowujemy wtedy kartę: adres jest już
 * adresem docelowego ekranu, a nowy `index.html` wskazuje istniejące pliki, więc
 * użytkownik ląduje tam, dokąd szedł — na nowej wersji.
 *
 * ── Dlaczego w ogóle jest bezpiecznik ──────────────────────────────────────
 * Przeładowanie bez ograniczeń to pętla, gdy błąd NIE wynika ze starej karty
 * (serwer leży, plik naprawdę zniknął, zła konfiguracja pośrednika): każde
 * przeładowanie kończyłoby się tym samym błędem. Dlatego:
 *   • najwyżej raz na `MIN_ODSTEP_MS` w jednej karcie (znacznik w sessionStorage —
 *     przeżywa przeładowanie, nie przeżywa zamknięcia karty);
 *   • znacznik zapisujemy PRZED przeładowaniem;
 *   • gdy pamięci sesji nie da się odczytać ani zapisać (tryb prywatny, zablokowane
 *     dane witryn), NIE przeładowujemy — bez znacznika nie ma ochrony przed pętlą,
 *     a stan „jak dotąd" jest mniejszym złem niż pętla.
 * Gdy bezpiecznik odmówi, zdarzenie leci dalej bez zmian — dokładnie to, co było
 * przed tą zmianą. Świadomie NIE wołamy `preventDefault()`: fabryki `lazy()` w
 * `App.tsx` robią `.then((m) => ({ default: m.X }))`, więc przy zdławionym błędzie
 * poleciałby inny wyjątek, a nie udałoby się niczego uratować.
 *
 * ⚠️ To NIE jest ErrorBoundary: obsługuje wyłącznie błąd ładowania pliku ekranu.
 * Wyjątek z renderu dowolnego komponentu nadal odmontowuje drzewo.
 * ⚠️ PWA instruktora tego nie potrzebuje — jej service worker trzyma wszystkie
 * pliki w precache, więc stara karta ma co ładować.
 *
 * Pilnuje tego `scripts/__tests__/odswiezanie-po-bledzie-chunka.test.mjs` (logika,
 * pętla, nazwa zdarzenia względem zainstalowanego Vite i podpięcie w `main.tsx`).
 */

/** Klucz znacznika w sessionStorage — czas (ms) ostatniego automatycznego przeładowania. */
export const KLUCZ_ZNACZNIKA = 'zur-ops:odswiezenie-po-bledzie-chunka';

/** Najkrótszy odstęp między dwoma automatycznymi przeładowaniami jednej karty. */
export const MIN_ODSTEP_MS = 60_000;

/** Nazwa zdarzenia, które Vite rzuca na `window` przy nieudanym pobraniu pliku ekranu. */
export const ZDARZENIE_VITE = 'vite:preloadError';

export type WynikObslugi = 'przeladowano' | 'za-szybko' | 'brak-pamieci-sesji';

/**
 * Czy minęło dość czasu od poprzedniego przeładowania. Odstęp liczony MODUŁEM:
 * zegar cofnięty o krótką chwilę nadal znaczy „przed momentem", a znacznik z
 * odległej przyszłości (zegar poprawiony po fakcie) nie może blokować karty na zawsze.
 */
export function czyWolnoOdswiezyc(teraz: number, ostatnio: number | null): boolean {
  if (ostatnio === null) return true;
  return Math.abs(teraz - ostatnio) >= MIN_ODSTEP_MS;
}

export interface SrodowiskoOdswiezania {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  teraz: () => number;
  przeladuj: () => void;
  /** Tylko diagnostyka (konsola przeglądarki) — nie wpływa na decyzję. */
  log?: (komunikat: string, szczegoly?: unknown) => void;
}

/** Jedna decyzja po błędzie ładowania pliku ekranu. Zwraca, co zrobiła. */
export function obsluzBladChunka(srodowisko: SrodowiskoOdswiezania, przyczyna?: unknown): WynikObslugi {
  const { storage, teraz, przeladuj, log } = srodowisko;
  const t = teraz();

  let ostatnio: number | null = null;
  try {
    const surowe = storage.getItem(KLUCZ_ZNACZNIKA);
    if (surowe !== null) {
      const liczba = Number(surowe);
      // Śmieć w znaczniku = brak znacznika; i tak go zaraz nadpiszemy czasem.
      ostatnio = Number.isFinite(liczba) ? liczba : null;
    }
  } catch {
    log?.('[zur] Błąd ładowania ekranu, ale pamięć sesji niedostępna — nie przeładowuję (ochrona przed pętlą)', przyczyna);
    return 'brak-pamieci-sesji';
  }

  if (!czyWolnoOdswiezyc(t, ostatnio)) {
    log?.('[zur] Błąd ładowania ekranu zaraz po przeładowaniu — nie powtarzam (ochrona przed pętlą)', przyczyna);
    return 'za-szybko';
  }

  // Znacznik PRZED przeładowaniem: po przeładowaniu ta karta nie wykona już kodu.
  try {
    storage.setItem(KLUCZ_ZNACZNIKA, String(t));
  } catch {
    log?.('[zur] Błąd ładowania ekranu, ale nie da się zapisać znacznika — nie przeładowuję (ochrona przed pętlą)', przyczyna);
    return 'brak-pamieci-sesji';
  }

  log?.('[zur] Wdrożono nową wersję aplikacji — przeładowuję kartę', przyczyna);
  przeladuj();
  return 'przeladowano';
}

/** To, czego potrzebujemy od `window` — żeby test mógł podstawić atrapę. */
export interface OknoPrzegladarki {
  addEventListener(typ: string, nasluch: (zdarzenie: Event) => void): void;
  readonly location: Pick<Location, 'reload'>;
  readonly sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
}

/**
 * Podpina obsługę. Wołać RAZ, w `main.tsx`, PRZED pierwszym renderem — błąd
 * ładowania może wystąpić już przy starcie (pierwsza trasa też jest leniwa).
 */
export function zainstalujOdswiezaniePoBleduChunka(okno: OknoPrzegladarki): void {
  okno.addEventListener(ZDARZENIE_VITE, (zdarzenie) => {
    let storage: OknoPrzegladarki['sessionStorage'];
    try {
      // Samo odwołanie do sessionStorage rzuca SecurityError przy zablokowanych danych witryn.
      storage = okno.sessionStorage;
    } catch {
      console.warn('[zur] Błąd ładowania ekranu, ale pamięć sesji niedostępna — nie przeładowuję (ochrona przed pętlą)');
      return;
    }
    obsluzBladChunka(
      {
        storage,
        teraz: () => Date.now(),
        przeladuj: () => okno.location.reload(),
        log: (komunikat, szczegoly) => console.warn(komunikat, szczegoly),
      },
      (zdarzenie as Event & { payload?: unknown }).payload,
    );
  });
}
